import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import NuevoTurnoModal from "@/components/NuevoTurnoModal";
import { ChevronLeft, ChevronRight, Plus, Clock } from "lucide-react";
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameDay,
  isSameMonth,
  addMonths,
  subMonths,
  isToday,
  startOfWeek,
  endOfWeek,
} from "date-fns";
import { es } from "date-fns/locale";

const API = "/api";

const DAYS = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

const EstadoBadge = ({ estado }) => {
  const cls = {
    pendiente: "badge-pendiente",
    confirmado: "badge-confirmado",
    completado: "badge-completado",
    cancelado: "badge-cancelado",
  };
  return <span className={cls[estado] || "badge-pendiente"}>{estado || "pendiente"}</span>;
};

// Parse flexible date text to Date object
const parseDate = (dateStr) => {
  if (!dateStr) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return new Date(dateStr + "T00:00:00");
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
    const [d, m, y] = dateStr.split("/");
    return new Date(`${y}-${m}-${d}T00:00:00`);
  }
  if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
    const [d, m, y] = dateStr.split("-");
    return new Date(`${y}-${m}-${d}T00:00:00`);
  }
  return null;
};

export default function CalendarPage() {
  const { getAuthHeader } = useAuth();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [turnos, setTurnos] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/turnos?limit=500`, { headers: getAuthHeader() });
      setTurnos(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Build calendar grid
  const calendarDays = useMemo(() => {
    const start = startOfWeek(startOfMonth(currentMonth), { weekStartsOn: 0 });
    const end = endOfWeek(endOfMonth(currentMonth), { weekStartsOn: 0 });
    return eachDayOfInterval({ start, end });
  }, [currentMonth]);

  // Map turnos to dates
  const turnosByDate = useMemo(() => {
    const map = {};
    turnos.forEach((t) => {
      const d = parseDate(t.fecha_turno);
      if (!d) return;
      const key = format(d, "yyyy-MM-dd");
      if (!map[key]) map[key] = [];
      map[key].push(t);
    });
    return map;
  }, [turnos]);

  const selectedKey = format(selectedDate, "yyyy-MM-dd");
  const selectedTurnos = turnosByDate[selectedKey] || [];

  return (
    <div className="p-4 md:p-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Calendario</h1>
          <p className="text-slate-500 text-sm mt-0.5">Gestión de turnos por fecha</p>
        </div>
        <button className="btn-primary" onClick={() => setShowModal(true)} data-testid="nuevo-turno-calendario-btn">
          <Plus size={16} />
          Nuevo turno
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <div className="lg:col-span-2 bg-white border border-slate-100 rounded-2xl p-6">
          {/* Month nav */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => setCurrentMonth((m) => subMonths(m, 1))}
              className="p-2 rounded-lg hover:bg-slate-100 text-slate-600 transition-colors"
              data-testid="prev-month-btn"
            >
              <ChevronLeft size={18} />
            </button>
            <h2 className="text-lg font-bold text-slate-900 capitalize">
              {format(currentMonth, "MMMM yyyy", { locale: es })}
            </h2>
            <button
              onClick={() => setCurrentMonth((m) => addMonths(m, 1))}
              className="p-2 rounded-lg hover:bg-slate-100 text-slate-600 transition-colors"
              data-testid="next-month-btn"
            >
              <ChevronRight size={18} />
            </button>
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7 mb-2">
            {DAYS.map((d) => (
              <div key={d} className="text-center text-xs font-bold uppercase text-slate-400 py-2">
                {d}
              </div>
            ))}
          </div>

          {/* Days grid */}
          {loading ? (
            <div className="grid grid-cols-7 gap-1">
              {Array(35).fill(0).map((_, i) => (
                <div key={i} className="h-14 bg-slate-50 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-7 gap-1">
              {calendarDays.map((day) => {
                const key = format(day, "yyyy-MM-dd");
                const dayTurnos = turnosByDate[key] || [];
                const isCurrentMonth = isSameMonth(day, currentMonth);
                const isSelected = isSameDay(day, selectedDate);
                const isToday_ = isToday(day);

                return (
                  <div
                    key={key}
                    onClick={() => setSelectedDate(day)}
                    className={`calendar-day ${isToday_ && !isSelected ? "today" : ""} ${isSelected ? "selected" : ""} ${dayTurnos.length > 0 ? "has-turnos" : ""}`}
                    data-testid={`calendar-day-${key}`}
                  >
                    <span className={`text-sm font-medium ${!isCurrentMonth ? "opacity-30" : ""} ${isSelected ? "text-white" : isToday_ ? "text-blue-600" : "text-slate-700"}`}>
                      {format(day, "d")}
                    </span>
                    {dayTurnos.length > 0 && (
                      <div className="flex gap-0.5 mt-1 flex-wrap justify-center">
                        {dayTurnos.slice(0, 3).map((_, i) => (
                          <div key={i} className="day-dot" />
                        ))}
                      </div>
                    )}
                    {dayTurnos.length > 0 && (
                      <span className={`text-xs mt-0.5 font-semibold ${isSelected ? "text-white/80" : "text-blue-600"}`}>
                        {dayTurnos.length}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Selected day turnos */}
        <div className="bg-white border border-slate-100 rounded-2xl p-6 flex flex-col">
          <div className="mb-4">
            <h3 className="font-bold text-slate-900">
              {format(selectedDate, "EEEE d 'de' MMMM", { locale: es })}
            </h3>
            <p className="text-xs text-slate-400 mt-0.5">{selectedTurnos.length} turno{selectedTurnos.length !== 1 ? "s" : ""}</p>
          </div>

          {selectedTurnos.length === 0 ? (
            <div className="flex flex-col items-center justify-center flex-1 py-10 text-center">
              <Clock size={32} className="text-slate-200 mb-3" />
              <p className="text-sm text-slate-400">Sin turnos este día</p>
              <button
                onClick={() => setShowModal(true)}
                className="mt-4 text-sm text-blue-600 hover:underline font-medium"
              >
                + Agendar turno
              </button>
            </div>
          ) : (
            <div className="space-y-3 overflow-auto flex-1">
              {selectedTurnos
                .sort((a, b) => (a.hora_turno || "").localeCompare(b.hora_turno || ""))
                .map((t) => (
                  <div key={t.id} className="p-3 rounded-xl border border-slate-100 hover:border-blue-100 hover:bg-blue-50/30 transition-colors" data-testid="calendar-turno-item">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-semibold text-slate-900 text-sm truncate">
                          {t.nombre} {t.apellido}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {t.hora_turno && <span className="mr-2">{t.hora_turno}</span>}
                          {t.servicio}
                        </p>
                        {t.cobertura && (
                          <p className="text-xs text-slate-400 mt-0.5">{t.cobertura}</p>
                        )}
                      </div>
                      <EstadoBadge estado={t.estado} />
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>

      <NuevoTurnoModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSuccess={load}
        initialDate={format(selectedDate, "yyyy-MM-dd")}
      />
    </div>
  );
}
